export const getLatestValidQuarter = (year: any) => {
  if (!year?.quarters) return null;

  for (let i = year.quarters.length - 1; i >= 0; i--) {
    const q = year.quarters[i];
    if (q?.marketCapitalisation?.value && q.marketCapitalisation.value !== 0) {
      return q;
    }
  }

  return null;
};

// export const formatNumber = (value: any): string | number => {
//   if (typeof value === "number") return value === 0 ? "-" : value.toFixed(2);
//   const num = Number(value ?? NaN);
//   return isNaN(num) || num === 0 ? "-" : num.toFixed(2);
// };

export const formatValue = (val?: string | number | boolean) =>
  typeof val === "boolean" ? (val ? "Yes" : "No") : val ?? "-";

export const formatListingDate = (dateString?: string): string => {
  if (!dateString) return "";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "numeric",
  };

  return new Intl.DateTimeFormat("en-GB", options).format(date);
};

export const safeFormat = (val: any): string => {
  if (
    val === null ||
    val === undefined ||
    (typeof val === "string" && val.trim() === "")
  ) {
    return "-";
  }

  const num = Number(val);

  if (!isNaN(num)) {
    if (num === 0) return "-";

    if (num < 0) return "-";

    return num.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  }

  return String(val);
};

export function formatLabel(label: string): string {
  switch (label) {
    case "Market Capitalisation":
      return "Market Capitalization (₹ Cr.)";
    case "Gross asset value":
      return "Gross Asset Value (₹ Cr.)";
    case "Total Portfolios":
      return "Total Portfolio (msf)";
    case "Complete area":
      return "Completed Area (msf)";
    case "Commercial Office Space, Hotel, Solar Power Plant":
      return "Commercial Office Space, Hotels, Solar Power Plant";
    case "Commercial Office Space, Business Park":
      return "Commercial Office Space, Business Parks";
    case "Commercial Office Space, Shopping Mall":
      return "Commercial Office Space, Shopping Malls";
    case "Shopping Mall, Commercial Office Space, Hotel, Solar Generation Plant 15 MW":
      return "Shopping Malls, Commercial Office Space, Hotels, Solar Generation Plant";
    default:
      return label;
  }
}

// Utility function
export const formatNumber = (
  value: number | string | null | undefined,
  isPercent: boolean = false
) => {
  if (value === null || value === undefined || value === "" || value === 0)
    return "—";

  // If pure string and NOT numeric → return original string
  if (typeof value === "string" && isNaN(Number(value))) {
    return value;
  }

  const num = Number(value);
  if (isNaN(num)) return value; // Also handles mixed strings

  const formatted = num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return isPercent ? `${formatted}%` : formatted;
};

// Helper function to safely convert to number
export const toNumber = (
  value: string | number | boolean | null | undefined
): number => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return value;
  if (typeof value === "boolean") return 0;
  const parsed = parseFloat(value.toString());
  return isNaN(parsed) ? 0 : parsed;
};

// Helper function to format currency
export const formatCurrency = (value: number): string => {
  if (value === 0) return "-";
  return `₹ ${value.toLocaleString("en-IN")} Mn.`;
};

export const formatPerUnit = (value: number): string => {
  if (value === 0 || value === null || value === undefined) return "-";
  return `₹ ${value.toLocaleString("en-IN")} p.u.`;
};


export const wrapText = (text: any, maxLength = 30) => {
  if (text === null || text === undefined) return "—";

  const str = String(text);

  const words = str.split(" ");
  const lines: string[] = [];
  let currentLine = "";

  for (const word of words) {
    if ((currentLine + word).length > maxLength) {
      lines.push(currentLine.trim());
      currentLine = word + " ";
    } else {
      currentLine += word + " ";
    }
  }

  if (currentLine.trim()) lines.push(currentLine.trim());

  return lines.join("\n");
};

export function findLatestValidValue(financialMetrics: any[], key: string) {
  if (!financialMetrics || financialMetrics.length === 0) return null;

  // Traverse years from latest to oldest
  for (let y = financialMetrics.length - 1; y >= 0; y--) {
    const year = financialMetrics[y];

    if (!year?.quarters || year.quarters.length === 0) continue;

    // Traverse quarters from latest to oldest
    for (let q = year.quarters.length - 1; q >= 0; q--) {
      const quarter = year.quarters[q];
      const value = quarter?.[key]?.value;

      if (
        value !== null &&
        value !== undefined &&
        value !== "" &&
        value !== 0
      ) {
        return value; // ✅ Return the first valid found
      }
    }
  }

  return null; // ❌ Nothing found across all years + quarters
}

export function findLatestSemiAnnualValue(opMetrics: any[], key: string) {
  if (!opMetrics || opMetrics.length === 0) return null;

  const sorted = [...opMetrics].sort((a, b) => {
    const yearA = parseInt(a.year?.replace(/\D/g, "") || "0");
    const yearB = parseInt(b.year?.replace(/\D/g, "") || "0");
    return yearA - yearB;
  });

  const latestYear = sorted[sorted.length - 1];
  if (!latestYear?.semiAnnuals || latestYear.semiAnnuals.length === 0)
    return null;

  for (let i = latestYear.semiAnnuals.length - 1; i >= 0; i--) {
    const semi = latestYear.semiAnnuals[i];

    const value = semi?.sectorOfOperation?.[key]?.value;

    if (value !== null && value !== undefined && value !== "" && value !== 0) {
      return value;
    }
  }

  return null;
}
export const getValidYears = (unitHoldingPattern: any[]) => {
  if (!unitHoldingPattern || !Array.isArray(unitHoldingPattern)) return [];

  return unitHoldingPattern
    .map((y) => y?.year)
    .filter((year) => {
      if (!year || typeof year !== "string") return false;

      // Exclude "NaN" or empty values
      if (year.includes("NaN") || year.trim() === "") return false;

      // Accept FY2024, FY 2024, FY 2024-25, FY24, FY 24-25 etc.
      const matches = year.match(/\d{2,4}/g);
      return !!matches && matches.length > 0;
    })
    .sort((a, b) => {
      // Extract first 4-digit or 2-digit number
      const yearA = parseInt(a.match(/\d{2,4}/)?.[0] || "0", 10);
      const yearB = parseInt(b.match(/\d{2,4}/)?.[0] || "0", 10);
      return yearB - yearA;
    });
};

export const formatDisplayValue = (val: any): string => {
  if (val === null || val === undefined || val === "" || val === 0) return "—";

  // Handle percentage strings like "87.4567%"
  if (typeof val === "string" && val.trim().endsWith("%")) {
    const numPart = parseFloat(val);
    if (isNaN(numPart)) return val;
    return `${numPart.toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    })}%`;
  }

  const num = Number(val);

  // Non-numeric
  if (isNaN(num)) return String(val);

  // Treat numeric 0 again just to be safe
  if (num === 0) return "—";

  // Whole numbers — no decimals
  if (Number.isInteger(num)) {
    return num.toLocaleString("en-IN");
  }

  // Decimal numbers — up to 2 decimals
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

// Function to convert decimal value into whole number
export const toWholeNumber = (value: any): string => {
  if (value === null || value === undefined || value === "") return "—";

  const num = Number(value);
  if (isNaN(num)) return String(value);

  return Math.round(num).toLocaleString("en-IN");
};

export const normalizeLocation = (name = "") => {
  const n = name.trim().toLowerCase();
  
  if (n === "ludhania") return "ludhiana";

  return n;
};

export const formatMarketPrice = (val: any): string => {
  if (
    val === null ||
    val === undefined ||
    (typeof val === "string" && val.trim() === "")
  ) {
    return "-";
  }

  const num = Number(val);

  if (!isNaN(num)) {
    if (num === 0) return "0";

    return num.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  }

  return String(val);
};
